import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API, formatApiErrorDetail } from "../App";
import AppShell from "./AppShell";
import axios from "axios";
import { 
  Save, Upload, Trash2, Image, Palette, Eye, Loader2, Mail, Smartphone, Download
} from "lucide-react";

const EmailPreviewSection = () => {
  const [previewHtml, setPreviewHtml] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const loadPreview = async () => {
    if (previewHtml) {
      setOpen(!open);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/email/preview`);
      setPreviewHtml(data.html);
      setOpen(true);
    } catch {
      setPreviewHtml("<p style='color:#DC2626;padding:16px;'>Failed to load preview.</p>");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6 mt-6" data-testid="email-preview-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-[17px] text-[#0A0A0A]">Email Preview</h2>
          <p className="text-[14px] text-[#6B7280] mt-0.5">See how invoice emails look to your clients</p>
        </div>
        <button
          onClick={loadPreview}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-[13px] font-semibold hover:bg-[#1A1A1A] transition-colors disabled:opacity-50"
          data-testid="email-preview-toggle"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          {open ? "Hide" : "Preview"}
        </button>
      </div>
      {open && previewHtml && (
        <div className="mt-4 border border-[#E5E7EB] bg-[#F9FAFB]">
          <div className="px-3 py-2 border-b border-[#E5E7EB] bg-white flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-[#9CA3AF]" />
            <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Inbox Preview</span>
          </div>
          <div className="p-4">
            <div className="bg-white border border-[#E5E7EB] shadow-sm">
              <div className="px-4 py-3 border-b border-[#E5E7EB] bg-[#FAFAFA]">
                <p className="text-[12px] text-[#9CA3AF]"><strong className="text-[#6B7280]">From:</strong> Your Business &lt;invoices@verobill.com&gt;</p>
                <p className="text-[12px] text-[#9CA3AF]"><strong className="text-[#6B7280]">To:</strong> client@example.com</p>
                <p className="text-[12px] text-[#9CA3AF]"><strong className="text-[#6B7280]">Subject:</strong> Invoice INV-0042 from Your Business</p>
              </div>
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
            <p className="text-[11px] text-[#9CA3AF] mt-3">This is a sample. The actual email will use your business name, client details, and invoice data.</p>
          </div>
        </div>
      )}
    </div>
  );
};

const Settings = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: user?.name || "",
    business_name: user?.business_name || "",
    business_initials: user?.business_initials || "",
    business_address: user?.business_address || "",
    business_phone: user?.business_phone || "",
    bank_name: user?.bank_name || "",
    account_number: user?.account_number || "",
    routing_number: user?.routing_number || "",
    paypal_email: user?.paypal_email || "",
    payment_notes: user?.payment_notes || "",
    show_verobill_branding: user?.show_verobill_branding !== false,
    custom_sender_email: user?.custom_sender_email || "",
    customer_stripe_secret_key: user?.customer_stripe_secret_key || "",
    customer_stripe_publishable_key: user?.customer_stripe_publishable_key || "",
    paypal_me_username: user?.paypal_me_username || "",
    customer_resend_api_key: user?.customer_resend_api_key || "",
    razorpay_key_id: user?.razorpay_key_id || "",
    razorpay_key_secret: user?.razorpay_key_secret || "",
    wise_email: user?.wise_email || "",
    default_country: user?.default_country || "",
    brand_color: user?.brand_color || "#1A3DFF",
    pdf_template: user?.pdf_template || "classic"
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        business_name: user.business_name || "",
        business_initials: user.business_initials || "",
        business_address: user.business_address || "",
        business_phone: user.business_phone || "",
        bank_name: user.bank_name || "",
        account_number: user.account_number || "",
        routing_number: user.routing_number || "",
        paypal_email: user.paypal_email || "",
        payment_notes: user.payment_notes || "",
        show_verobill_branding: user.show_verobill_branding !== false,
        customer_stripe_secret_key: user.customer_stripe_secret_key || "",
        customer_stripe_publishable_key: user.customer_stripe_publishable_key || "",
        paypal_me_username: user.paypal_me_username || "",
        customer_resend_api_key: user.customer_resend_api_key || "",
        razorpay_key_id: user.razorpay_key_id || "",
        razorpay_key_secret: user.razorpay_key_secret || "",
        wise_email: user.wise_email || "",
        default_country: user.default_country || "",
        brand_color: user.brand_color || "#1A3DFF",
        pdf_template: user.pdf_template || "classic"
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setSaving(true);

    try {
      const { data } = await axios.put(`${API}/user/settings`, formData);
      setUser(prev => ({ ...prev, ...data }));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Image must be under 2MB");
      return;
    }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/user/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setUser(prev => ({ ...prev, ...data }));
    } catch (err) {
      setError("Failed to upload logo");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    try {
      const { data } = await axios.delete(`${API}/user/logo`);
      setUser(prev => ({ ...prev, ...data, logo_url: null }));
    } catch (err) {
      setError("Failed to remove logo");
    }
  };

  return (
    <AppShell active="settings">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="font-display text-[22px] sm:text-[28px] tracking-[-0.03em] text-[#0A0A0A]">
          Settings
        </h1>
        <p className="text-[13px] sm:text-[14px] text-[#6B7280] mt-1">Manage your account and business details</p>
      </div>

      {/* Settings Form */}
      <div className="max-w-xl">
        <form onSubmit={handleSubmit} className="bg-white border border-[#E5E7EB] p-4 sm:p-6 space-y-5">
          <div>
            <h2 className="font-semibold text-[17px] text-[#0A0A0A] mb-1">Business information</h2>
            <p className="text-[14px] text-[#6B7280] mb-6">This information will appear on your invoices.</p>
          </div>

          {error && (
            <div className="p-4 bg-[#FEF2F2] border border-[#FECACA] text-[#DC2626] text-[14px]" data-testid="settings-error">
              {error}
            </div>
          )}

          {success && (
            <div className="p-4 bg-[#ECFDF5] border border-[#A7F3D0] text-[#059669] text-[14px]" data-testid="settings-success">
              Settings saved successfully
            </div>
          )}

          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Your name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="input text-[14px]"
              data-testid="settings-name"
            />
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Business name</label>
            <input
              type="text"
              value={formData.business_name}
              onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
              className="input text-[14px]"
              placeholder="Your Company Ltd."
              data-testid="settings-business-name"
            />
            <p className="text-[12px] text-[#9CA3AF] mt-1.5">This will be displayed at the top of your invoices</p>
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Logo initials</label>
            <div className="flex items-center gap-4">
              <input
                type="text"
                value={formData.business_initials}
                onChange={(e) => setFormData(prev => ({ ...prev, business_initials: e.target.value.toUpperCase().slice(0, 3) }))}
                className="input text-[14px] w-24 text-center font-bold uppercase"
                placeholder="AC"
                maxLength={3}
                data-testid="settings-initials"
              />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1A3DFF] flex items-center justify-center">
                  <span className="text-white font-bold text-[11px]">{formData.business_initials || "VB"}</span>
                </div>
                <span className="text-[12px] text-[#9CA3AF] hidden sm:inline">Preview - appears on your invoices</span>
              </div>
            </div>
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Business logo</label>
            <div className="flex items-center gap-4">
              {user?.logo_url ? (
                <div className="relative group">
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${user.logo_url}`} 
                    alt="Logo" 
                    className="w-14 h-14 object-contain border border-[#E5E7EB]"
                    data-testid="logo-preview"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <button
                    type="button"
                    onClick={handleLogoDelete}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-[#EF4444] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid="logo-delete-btn"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-14 h-14 border border-dashed border-[#D1D5DB] flex items-center justify-center bg-[#F9FAFB]">
                  <Image className="w-5 h-5 text-[#9CA3AF]" />
                </div>
              )}
              <div>
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-[#E5E7EB] text-[13px] font-medium text-[#6B7280] hover:bg-[#F9FAFB] transition-colors">
                  <Upload className="w-3.5 h-3.5" />
                  {logoUploading ? "Uploading..." : user?.logo_url ? "Replace" : "Upload"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={logoUploading}
                    data-testid="logo-upload-input"
                  />
                </label>
                <p className="text-[11px] text-[#9CA3AF] mt-1">PNG, JPG, SVG. Max 2MB.</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Business address</label>
            <textarea
              value={formData.business_address}
              onChange={(e) => setFormData(prev => ({ ...prev, business_address: e.target.value }))}
              className="input resize-none text-[14px]"
              rows={3}
              placeholder="123 Business St, Suite 100&#10;City, State 12345"
              data-testid="settings-address"
            />
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Business phone</label>
            <input
              type="tel"
              value={formData.business_phone}
              onChange={(e) => setFormData(prev => ({ ...prev, business_phone: e.target.value }))}
              className="input text-[14px]"
              placeholder="+1 (555) 123-4567"
              data-testid="settings-phone"
            />
          </div>
        </form>

        {/* Payment Information */}
        <form onSubmit={handleSubmit} className="bg-white border border-[#E5E7EB] p-4 sm:p-6 mt-6 space-y-5">
          <div>
            <h2 className="font-semibold text-[17px] text-[#0A0A0A] mb-1">Payment information</h2>
            <p className="text-[14px] text-[#6B7280] mb-6">This will appear on your invoices so clients know how to pay you.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Bank name</label>
              <input
                type="text"
                value={formData.bank_name}
                onChange={(e) => setFormData(prev => ({ ...prev, bank_name: e.target.value }))}
                className="input text-[14px]"
                placeholder="Chase Bank"
                data-testid="settings-bank-name"
              />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Account number</label>
              <input
                type="text"
                value={formData.account_number}
                onChange={(e) => setFormData(prev => ({ ...prev, account_number: e.target.value }))}
                className="input text-[14px]"
                placeholder="****1234"
                data-testid="settings-account-number"
              />
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Routing number / Sort code</label>
            <input
              type="text"
              value={formData.routing_number}
              onChange={(e) => setFormData(prev => ({ ...prev, routing_number: e.target.value }))}
              className="input text-[14px]"
              placeholder="123456789"
              data-testid="settings-routing-number"
            />
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">PayPal email</label>
            <input
              type="email"
              value={formData.paypal_email}
              onChange={(e) => setFormData(prev => ({ ...prev, paypal_email: e.target.value }))}
              className="input text-[14px]"
              placeholder="payments@yourbusiness.com"
              data-testid="settings-paypal"
            />
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Payment notes</label>
            <textarea
              value={formData.payment_notes}
              onChange={(e) => setFormData(prev => ({ ...prev, payment_notes: e.target.value }))}
              className="input resize-none text-[14px]"
              rows={2}
              placeholder="Payment due within 30 days. Late fees may apply."
              data-testid="settings-payment-notes"
            />
          </div>

          <div className="pt-4 border-t border-[#E5E7EB] flex items-center gap-4">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
              data-testid="save-payment-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save changes"}
            </button>
            {success && (
              <span className="text-[14px] font-medium text-[#059669]" data-testid="save-success-inline">Saved!</span>
            )}
          </div>
        </form>

        {/* Branding & PDF Templates */}
        <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6 mt-6 space-y-6">
          <div>
            <h2 className="font-semibold text-[17px] text-[#0A0A0A] mb-1 flex items-center gap-2"><Palette className="w-4 h-4" /> Branding & PDF Templates</h2>
            <p className="text-[14px] text-[#6B7280] mb-4">Customize your invoice PDF appearance with your brand color and template style.</p>
          </div>

          {/* Brand Color */}
          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Brand color</label>
            <p className="text-[12px] text-[#6B7280] mb-3">Used for accents, headers, and highlights on your invoice PDFs.</p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <input
                  type="color"
                  value={formData.brand_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, brand_color: e.target.value }))}
                  className="w-10 h-10 border border-[#E5E7EB] cursor-pointer p-0.5"
                  data-testid="brand-color-picker"
                />
              </div>
              <input
                type="text"
                value={formData.brand_color}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setFormData(prev => ({ ...prev, brand_color: v }));
                }}
                className="input text-[13px] font-mono w-28 uppercase"
                maxLength={7}
                data-testid="brand-color-hex"
              />
              {/* Quick presets */}
              {["#1A3DFF", "#0A0A0A", "#10B981", "#EF4444", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4"].map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, brand_color: c }))}
                  className={`w-7 h-7 border-2 transition-all ${formData.brand_color === c ? "border-[#0A0A0A] scale-110" : "border-transparent hover:border-[#D1D5DB]"}`}
                  style={{ backgroundColor: c }}
                  data-testid={`brand-preset-${c.slice(1)}`}
                />
              ))}
            </div>
          </div>

          {/* PDF Template */}
          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Invoice template</label>
            <p className="text-[12px] text-[#6B7280] mb-3">Choose a layout for your PDF invoices. All templates use your brand color.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { id: "classic", name: "Classic", desc: "Logo + name side by side, clean table", pro: false },
                { id: "modern", name: "Modern", desc: "Bold color banner header, striped rows", pro: true },
                { id: "minimal", name: "Minimal", desc: "Text-only header, thin accent lines", pro: true }
              ].map(t => {
                const isFreeUser = !user?.plan || user.plan === "free";
                const isLocked = t.pro && isFreeUser;
                return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => !isLocked && setFormData(prev => ({ ...prev, pdf_template: t.id }))}
                  className={`text-left p-4 border-2 transition-all relative ${isLocked ? "border-[#E5E7EB] opacity-60 cursor-not-allowed" : formData.pdf_template === t.id ? "border-[#0A0A0A] bg-[#F9FAFB]" : "border-[#E5E7EB] hover:border-[#D1D5DB]"}`}
                  data-testid={`template-${t.id}`}
                >
                  {isLocked && (
                    <span className="absolute top-2 right-2 text-[10px] font-bold text-white bg-[#1A3DFF] px-1.5 py-0.5 uppercase tracking-wide">Pro</span>
                  )}
                  {/* Mini preview bar */}
                  <div className="mb-3 h-6 flex items-stretch gap-1">
                    {t.id === "classic" && (
                      <>
                        <div className="w-6 h-6" style={{ backgroundColor: formData.brand_color }} />
                        <div className="flex-1 bg-[#F3F4F6] h-3 mt-1.5" />
                      </>
                    )}
                    {t.id === "modern" && (
                      <div className="w-full h-6 rounded-sm" style={{ backgroundColor: formData.brand_color }} />
                    )}
                    {t.id === "minimal" && (
                      <div className="w-full flex flex-col justify-end">
                        <div className="h-0.5 w-full" style={{ backgroundColor: formData.brand_color }} />
                      </div>
                    )}
                  </div>
                  <p className="text-[13px] font-bold text-[#0A0A0A]">{t.name}</p>
                  <p className="text-[11px] text-[#6B7280] mt-0.5">{t.desc}</p>
                </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              axios.put(`${API}/user/settings`, { brand_color: formData.brand_color, pdf_template: formData.pdf_template }).then(({ data }) => {
                setUser(prev => ({ ...prev, ...data }));
                setSuccess(true);
                setTimeout(() => setSuccess(false), 3000);
              }).catch(() => setError("Failed to save branding"));
            }}
            className="btn-primary text-[13px] px-4 py-2.5 flex items-center gap-2"
            data-testid="save-branding-btn"
          >
            <Save className="w-3.5 h-3.5" /> Save branding
          </button>

          {/* Show VeroBill branding toggle */}
          <div className="flex items-center justify-between py-3 border-t border-[#E5E7EB]">
            <div className="flex-1 min-w-0 mr-4">
              <p className="text-[14px] font-medium text-[#0A0A0A]">Show "Powered by VeroBill"</p>
              <p className="text-[13px] text-[#6B7280] mt-0.5">Display a subtle VeroBill footer on invoices and shared links</p>
              {(!user?.plan || user.plan === "free") && (
                <p className="text-[11px] font-bold text-[#1A3DFF] mt-1">Pro feature — upgrade to remove branding</p>
              )}
            </div>
            <button
              type="button"
              disabled={!user?.plan || user.plan === "free"}
              onClick={() => {
                const newVal = !formData.show_verobill_branding;
                setFormData(prev => ({ ...prev, show_verobill_branding: newVal }));
                axios.put(`${API}/user/settings`, { show_verobill_branding: newVal }).then(({ data }) => {
                  setUser(prev => ({ ...prev, ...data }));
                });
              }}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${(!user?.plan || user.plan === "free") ? 'bg-[#E5E7EB] cursor-not-allowed opacity-50' : formData.show_verobill_branding ? 'bg-[#1A3DFF]' : 'bg-[#D1D5DB]'}`}
              data-testid="branding-toggle"
            >
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.show_verobill_branding ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
        </div>

        {/* Email Integration */}
        <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6 mt-6">
          <h2 className="font-semibold text-[17px] text-[#0A0A0A] mb-1">Invoice Defaults</h2>
          <p className="text-[14px] text-[#6B7280] mb-5">Set your country for automatic tax configuration on new invoices.</p>
          
          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Default country / tax region</label>
            <select
              value={formData.default_country || ""}
              onChange={(e) => {
                const val = e.target.value;
                setFormData(prev => ({ ...prev, default_country: val }));
                axios.put(`${API}/user/settings`, { default_country: val }).then(({ data }) => {
                  setUser(prev => ({ ...prev, ...data }));
                  setSuccess(true);
                  setTimeout(() => setSuccess(false), 3000);
                });
              }}
              className="input text-[14px] max-w-xs"
              data-testid="settings-country"
            >
              <option value="">No default (manual tax)</option>
              <option value="US">United States (Sales Tax)</option>
              <option value="UK">United Kingdom (VAT 20%)</option>
              <option value="EU">European Union (VAT 21%)</option>
              <option value="DE">Germany (MwSt 19%)</option>
              <option value="FR">France (TVA 20%)</option>
              <option value="IN">India (GST / CGST+SGST)</option>
              <option value="CA">Canada (GST + PST)</option>
              <option value="AU">Australia (GST 10%)</option>
              <option value="SG">Singapore (GST 9%)</option>
              <option value="AE">UAE (VAT 5%)</option>
              <option value="JP">Japan (Consumption Tax 10%)</option>
              <option value="BR">Brazil (ICMS + ISS)</option>
            </select>
            <p className="text-[12px] text-[#9CA3AF] mt-1.5">This pre-fills tax lines when creating invoices. You can always override per invoice.</p>
          </div>

          {formData.default_country && (
            <div className="mt-4">
              <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">
                Tax registration number
                <span className="text-[12px] font-normal text-[#6B7280] ml-1">
                  ({formData.default_country === "IN" ? "GSTIN" : formData.default_country === "UK" ? "VAT Number" : formData.default_country === "AU" ? "ABN" : formData.default_country === "CA" ? "GST/HST Number" : formData.default_country === "JP" ? "Invoice Reg. No." : formData.default_country === "BR" ? "CNPJ" : "VAT ID"})
                </span>
              </label>
              <input
                type="text"
                value={formData.tax_registration_number || user?.tax_registration_number || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, tax_registration_number: e.target.value }))}
                onBlur={() => {
                  if (formData.tax_registration_number !== undefined) {
                    axios.put(`${API}/user/settings`, { tax_registration_number: formData.tax_registration_number }).then(({ data }) => {
                      setUser(prev => ({ ...prev, ...data }));
                    });
                  }
                }}
                className="input text-[14px] max-w-xs font-mono"
                placeholder={formData.default_country === "IN" ? "22AAAAA0000A1Z5" : formData.default_country === "UK" ? "GB 123 4567 89" : formData.default_country === "AU" ? "12 345 678 901" : "Enter your tax ID"}
                data-testid="settings-tax-registration"
              />
              <p className="text-[12px] text-[#9CA3AF] mt-1.5">Appears on invoices and PDFs for tax compliance.</p>
            </div>
          )}
        </div>

        {/* Email Setup */}
        <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6 mt-6">
          <h2 className="font-semibold text-[17px] text-[#0A0A0A] mb-1">Email Setup</h2>
          <p className="text-[14px] text-[#6B7280] mb-5">Configure how invoice emails are sent to your clients.</p>
          
          {/* How it works explanation */}
          <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-5">
            <p className="text-[13px] font-semibold text-[#0A0A0A] mb-2">How email works</p>
            <div className="space-y-2.5 text-[13px] text-[#6B7280]">
              <div className="flex gap-2">
                <span className="font-mono text-[#1A3DFF] font-bold w-5 flex-shrink-0">1.</span>
                <p>Invoice emails are sent <strong className="text-[#0A0A0A]">from VeroBill</strong> on your behalf — no setup needed.</p>
              </div>
              <div className="flex gap-2">
                <span className="font-mono text-[#1A3DFF] font-bold w-5 flex-shrink-0">2.</span>
                <p>The email displays <strong className="text-[#0A0A0A]">your business name</strong> and any message you add when sending.</p>
              </div>
              <div className="flex gap-2">
                <span className="font-mono text-[#1A3DFF] font-bold w-5 flex-shrink-0">3.</span>
                <p>When a client hits "Reply", it goes directly to <strong className="text-[#0A0A0A]">your reply-to email</strong> below.</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Reply-to email address</label>
            <p className="text-[12px] text-[#6B7280] mb-3">Client replies go directly to this address. If empty, replies go to your account email.</p>
            <div className="flex gap-2">
              <input
                type="email"
                value={formData.custom_sender_email || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, custom_sender_email: e.target.value }))}
                className="input text-[14px] flex-1"
                placeholder="invoices@yourbusiness.com"
                data-testid="settings-custom-email"
              />
              <button
                type="button"
                onClick={() => {
                  axios.put(`${API}/user/settings`, { custom_sender_email: formData.custom_sender_email || "" }).then(({ data }) => {
                    setUser(prev => ({ ...prev, ...data }));
                    setSuccess(true);
                    setTimeout(() => setSuccess(false), 3000);
                  });
                }}
                className="px-4 py-2 text-[13px] font-semibold bg-[#0A0A0A] text-white hover:bg-[#1A3DFF] transition-colors flex-shrink-0"
                data-testid="save-custom-email-btn"
              >
                Save
              </button>
            </div>
          </div>
        </div>

        {/* Payment Integration */}
        <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6 mt-6">
          <h2 className="font-semibold text-[17px] text-[#0A0A0A] mb-1">Payment Integration</h2>
          <p className="text-[14px] text-[#6B7280] mb-5">Connect your payment accounts so clients can pay you directly from invoices.</p>
          
          {/* Stripe */}
          <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-[#635BFF] flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">S</span>
              </div>
              <span className="font-semibold text-[14px] text-[#0A0A0A]">Stripe</span>
              {formData.customer_stripe_secret_key ? (
                <span className="text-[11px] font-bold text-[#10B981] bg-[#ECFDF5] px-2 py-0.5 ml-auto" data-testid="stripe-connected-badge">Connected</span>
              ) : (
                <span className="text-[11px] font-bold text-[#9CA3AF] bg-[#F3F4F6] px-2 py-0.5 ml-auto">Not connected</span>
              )}
            </div>
            <p className="text-[12px] text-[#6B7280] mb-3">
              Add your Stripe API keys to receive card payments directly. Find them at{" "}
              <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-[#1A3DFF] hover:underline">dashboard.stripe.com/apikeys</a>
            </p>
            <div className="space-y-2">
              <div>
                <label className="text-[12px] font-semibold text-[#6B7280]">Publishable key</label>
                <input
                  type="text"
                  value={formData.customer_stripe_publishable_key || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_stripe_publishable_key: e.target.value }))}
                  className="input text-[13px] font-mono"
                  placeholder="pk_live_..."
                  data-testid="stripe-publishable-key"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#6B7280]">Secret key</label>
                <input
                  type="password"
                  value={formData.customer_stripe_secret_key || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_stripe_secret_key: e.target.value }))}
                  className="input text-[13px] font-mono"
                  placeholder="sk_live_..."
                  data-testid="stripe-secret-key"
                />
              </div>
            </div>
          </div>

          {/* PayPal */}
          <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-[#0070BA] flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">PP</span>
              </div>
              <span className="font-semibold text-[14px] text-[#0A0A0A]">PayPal</span>
            </div>
            <p className="text-[12px] text-[#6B7280] mb-3">
              Add your PayPal.me username. A "Pay with PayPal" button will appear on your invoices.
            </p>
            <div>
              <label className="text-[12px] font-semibold text-[#6B7280]">PayPal.me username</label>
              <div className="flex items-center gap-0">
                <span className="px-3 py-2.5 bg-[#E5E7EB] border border-r-0 border-[#D1D5DB] text-[13px] text-[#6B7280]">paypal.me/</span>
                <input
                  type="text"
                  value={formData.paypal_me_username || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, paypal_me_username: e.target.value }))}
                  className="input text-[13px] flex-1 border-l-0"
                  placeholder="yourusername"
                  data-testid="paypal-me-username"
                />
              </div>
            </div>
          </div>

          {/* Razorpay (UPI) */}
          <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-[#072654] flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">RZ</span>
              </div>
              <span className="font-semibold text-[14px] text-[#0A0A0A]">Razorpay (UPI)</span>
              {formData.razorpay_key_id && formData.razorpay_key_secret ? (
                <span className="text-[11px] font-bold text-[#10B981] bg-[#ECFDF5] px-2 py-0.5 ml-auto" data-testid="razorpay-connected-badge">Connected</span>
              ) : (
                <span className="text-[11px] font-bold text-[#9CA3AF] bg-[#F3F4F6] px-2 py-0.5 ml-auto">Not connected</span>
              )}
            </div>
            <p className="text-[12px] text-[#6B7280] mb-3">
              Accept UPI payments from Indian clients. Get keys from{" "}
              <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noopener noreferrer" className="text-[#1A3DFF] hover:underline">dashboard.razorpay.com</a>
            </p>
            <div className="space-y-2">
              <div>
                <label className="text-[12px] font-semibold text-[#6B7280]">Key ID</label>
                <input type="text" value={formData.razorpay_key_id || ""} onChange={(e) => setFormData(prev => ({ ...prev, razorpay_key_id: e.target.value }))} className="input text-[13px] font-mono" placeholder="rzp_live_..." data-testid="razorpay-key-id" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#6B7280]">Key Secret</label>
                <input type="password" value={formData.razorpay_key_secret || ""} onChange={(e) => setFormData(prev => ({ ...prev, razorpay_key_secret: e.target.value }))} className="input text-[13px] font-mono" placeholder="Secret key" data-testid="razorpay-key-secret" />
              </div>
            </div>
          </div>

          {/* Wise */}
          <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-[#9FE870] flex items-center justify-center">
                <span className="text-[#163300] text-[10px] font-bold">W</span>
              </div>
              <span className="font-semibold text-[14px] text-[#0A0A0A]">Wise</span>
            </div>
            <p className="text-[12px] text-[#6B7280] mb-3">
              Add your Wise email or profile link. A "Pay via Wise" button will appear on invoices.
            </p>
            <div>
              <label className="text-[12px] font-semibold text-[#6B7280]">Wise email or profile handle</label>
              <input type="text" value={formData.wise_email || ""} onChange={(e) => setFormData(prev => ({ ...prev, wise_email: e.target.value }))} className="input text-[13px]" placeholder="you@email.com or your-handle" data-testid="wise-email" />
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              const paymentData = {
                customer_stripe_secret_key: formData.customer_stripe_secret_key || "",
                customer_stripe_publishable_key: formData.customer_stripe_publishable_key || "",
                paypal_me_username: formData.paypal_me_username || "",
                razorpay_key_id: formData.razorpay_key_id || "",
                razorpay_key_secret: formData.razorpay_key_secret || "",
                wise_email: formData.wise_email || "",
              };
              axios.put(`${API}/user/settings`, paymentData).then(({ data }) => {
                setUser(prev => ({ ...prev, ...data }));
                setSuccess(true);
                setTimeout(() => setSuccess(false), 3000);
              }).catch(() => setError("Failed to save payment settings"));
            }}
            className="btn-primary text-[13px] px-4 py-2.5"
            data-testid="save-payment-integrations"
          >
            Save payment settings
          </button>
        </div>

        {/* Resend Integration */}
        <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6 mt-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-semibold text-[17px] text-[#0A0A0A]">Email Integration (Optional)</h2>
            {(!user?.plan || user.plan === "free") && (
              <span className="text-[10px] font-bold text-white bg-[#1A3DFF] px-1.5 py-0.5 uppercase tracking-wide">Pro</span>
            )}
          </div>
          <p className="text-[14px] text-[#6B7280] mb-5">By default, emails are sent via VeroBill. If you want emails sent from your own domain, add your Resend API key.</p>
          
          {(!user?.plan || user.plan === "free") ? (
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 text-center">
              <p className="text-[13px] text-[#6B7280] mb-2">Custom email domain is a Pro feature.</p>
              <Link to="/billing" className="text-[13px] font-bold text-[#1A3DFF] hover:underline">Upgrade to Pro</Link>
            </div>
          ) : (
          <>
          <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4">
            <p className="text-[13px] font-semibold text-[#0A0A0A] mb-2">How to set up your own email sending</p>
            <div className="space-y-2 text-[13px] text-[#6B7280] mb-4">
              <div className="flex gap-2">
                <span className="font-mono text-[#1A3DFF] font-bold w-5 flex-shrink-0">1.</span>
                <p>Sign up at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-[#1A3DFF] hover:underline">resend.com</a> (free — 3,000 emails/month)</p>
              </div>
              <div className="flex gap-2">
                <span className="font-mono text-[#1A3DFF] font-bold w-5 flex-shrink-0">2.</span>
                <p>Verify your domain in Resend (add DNS records)</p>
              </div>
              <div className="flex gap-2">
                <span className="font-mono text-[#1A3DFF] font-bold w-5 flex-shrink-0">3.</span>
                <p>Copy your API key from Resend dashboard and paste below</p>
              </div>
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#6B7280]">Resend API Key</label>
              <div className="flex gap-2">
                <input
                  type="password"
                  value={formData.customer_resend_api_key || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_resend_api_key: e.target.value }))}
                  className="input text-[13px] font-mono flex-1"
                  placeholder="re_..."
                  data-testid="resend-api-key"
                />
                <button
                  type="button"
                  onClick={() => {
                    axios.put(`${API}/user/settings`, { customer_resend_api_key: formData.customer_resend_api_key || "" }).then(({ data }) => {
                      setUser(prev => ({ ...prev, ...data }));
                      setSuccess(true);
                      setTimeout(() => setSuccess(false), 3000);
                    });
                  }}
                  className="px-4 py-2 text-[13px] font-semibold bg-[#0A0A0A] text-white hover:bg-[#1A3DFF] transition-colors flex-shrink-0"
                  data-testid="save-resend-key"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
          </>
          )}
        </div>

        {/* Email Preview */}
        <EmailPreviewSection />

        {/* Install App */}
        <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6 mt-6" data-testid="install-app-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#1A3DFF] flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-[17px] text-[#0A0A0A]">Install VeroBill on your phone</h2>
              <p className="text-[14px] text-[#6B7280] mt-0.5">Works like a native app. Invoice from anywhere.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {/* iOS */}
            <div className="border border-[#E5E7EB] p-4">
              <p className="text-[13px] font-semibold text-[#0A0A0A] mb-2">iPhone / iPad</p>
              <ol className="text-[13px] text-[#6B7280] space-y-1.5 list-decimal list-inside">
                <li>Open <strong className="text-[#0A0A0A]">verobill.com</strong> in Safari</li>
                <li>Tap the <strong className="text-[#0A0A0A]">Share</strong> button (bottom bar)</li>
                <li>Scroll down and tap <strong className="text-[#0A0A0A]">"Add to Home Screen"</strong></li>
                <li>Tap <strong className="text-[#0A0A0A]">Add</strong> — done</li>
              </ol>
            </div>

            {/* Android */}
            <div className="border border-[#E5E7EB] p-4">
              <p className="text-[13px] font-semibold text-[#0A0A0A] mb-2">Android</p>
              <ol className="text-[13px] text-[#6B7280] space-y-1.5 list-decimal list-inside">
                <li>Open <strong className="text-[#0A0A0A]">verobill.com</strong> in Chrome</li>
                <li>Tap the <strong className="text-[#0A0A0A]">three-dot menu</strong> (top right)</li>
                <li>Tap <strong className="text-[#0A0A0A]">"Install app"</strong> or "Add to Home Screen"</li>
                <li>Tap <strong className="text-[#0A0A0A]">Install</strong> — done</li>
              </ol>
              <div className="mt-3 p-2.5 bg-[#FFFBEB] border border-[#F59E0B]/20">
                <p className="text-[12px] text-[#92400E]">
                  <strong>Note:</strong> Google Play Protect may show an "Unsafe app" warning. This is normal for all web apps installed outside the Play Store. Tap <strong>"Install anyway"</strong> to proceed safely.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Account Details */}
        <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6 mt-6 mb-8">
          <h2 className="font-semibold text-[17px] text-[#0A0A0A] mb-4">Account</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Email</label>
              <p className="text-[14px] text-[#0A0A0A]">{user?.email}</p>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Account type</label>
              <p className="text-[14px] text-[#0A0A0A] capitalize">{user?.role || "User"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed toast notification */}
      {success && (
        <div className="fixed bottom-6 right-6 bg-[#ECFDF5] border border-[#10B981] px-6 py-3 flex items-center gap-2 shadow-lg z-50 animate-in" data-testid="save-toast">
          <Save className="w-4 h-4 text-[#059669]" />
          <span className="text-[14px] font-medium text-[#065F46]">Settings saved successfully</span>
        </div>
      )}
    </AppShell>
  );
};

export default Settings;
